/**
 * Who may manage whom (ADR-015 §7). Pure: the caller loads actor and target inside its transaction
 * (owners locked FOR UPDATE) and asks here. Every member operation goes through `canManage`, every
 * role assignment / edition / deletion through `assertCanGrant`.
 *
 * Permission required by operation:
 *
 * | Operation        | Permission         |
 * |------------------|--------------------|
 * | invite           | users.manage       |
 * | cancelInvitation | users.manage       |
 * | suspend          | users.manage       |
 * | reactivate       | users.manage       |
 * | disable          | users.manage       |
 * | changeRoles      | users.manage       |
 * | resendInvitation | users.reset_access |
 * | revokeSessions   | users.reset_access |
 * | changePhone      | users.reset_access |
 * | resetMfa         | users.reset_access |
 *
 * `roles.manage` is not required by any member operation: it guards role definitions (create, edit
 * permissions, delete), where `assertCanGrant` applies. Assigning existing roles to a person needs
 * `users.manage` and is limited by the containment rule below.
 *
 * Rules, in order (the first that fails is the reason):
 *  1. MISSING_PERMISSION        the actor holds the permission of the operation
 *  2. SELF                      nobody manages their own account here (own account: self-service flows)
 *  3. OWNER_ONLY                an owner (current or resulting) is only managed by another owner
 *  4. EXCEEDS_ACTOR_PERMISSIONS the target's permissions (current and resulting) are contained in the actor's
 *  5. LAST_OWNER                the tenant always keeps one active owner
 */
import { DRIVER_APP_PERMISSIONS, NOTIFY_ONLY_PERMISSIONS, type PermissionKey } from '../rbac/permissions';

export const MANAGE_OPERATIONS = [
  'invite',
  'resendInvitation',
  'cancelInvitation',
  'suspend',
  'reactivate',
  'disable',
  'changeRoles',
  'revokeSessions',
  'changePhone',
  'resetMfa',
] as const;
export type ManageOperation = (typeof MANAGE_OPERATIONS)[number];

export const MANAGE_OPERATION_PERMISSION: Readonly<Record<ManageOperation, PermissionKey>> = {
  invite: 'users.manage',
  cancelInvitation: 'users.manage',
  suspend: 'users.manage',
  reactivate: 'users.manage',
  disable: 'users.manage',
  changeRoles: 'users.manage',
  resendInvitation: 'users.reset_access',
  revokeSessions: 'users.reset_access',
  changePhone: 'users.reset_access',
  resetMfa: 'users.reset_access',
};

/** Operations after which the target may stop counting as an active owner. */
const MAY_REMOVE_OWNER: ReadonlySet<ManageOperation> = new Set(['suspend', 'disable', 'changeRoles', 'changePhone']);

export interface ManageActor {
  actorId: string;
  actorPermissions: ReadonlySet<string>;
  actorIsOwner: boolean;
}

export interface ManageTarget {
  /** null only for `invite`, when the account does not exist yet. */
  targetId: string | null;
  /** Permissions the target holds today (empty for a new invitation). */
  targetPermissions: ReadonlySet<string>;
  targetIsOwner: boolean;
  /** `invite` / `changeRoles`: permissions the target would hold afterwards. */
  nextPermissions?: ReadonlySet<string>;
  /** `invite` / `changeRoles`: whether the target would be an owner afterwards. */
  nextIsOwner?: boolean;
}

export interface ManageOptions {
  /**
   * Active owners the tenant would have after the operation, counted by the caller with the owner
   * rows locked. Mandatory when the operation may take an owner out (suspend, disable, changeRoles,
   * changePhone on an owner): leaving it out is denied.
   */
  activeOwnersAfter?: number;
}

export type ManageDenial = 'MISSING_PERMISSION' | 'SELF' | 'OWNER_ONLY' | 'EXCEEDS_ACTOR_PERMISSIONS' | 'LAST_OWNER';
export type ManageDecision = { ok: true } | { ok: false; reason: ManageDenial };

const NO_POWER = new Set<string>([...DRIVER_APP_PERMISSIONS, ...NOTIFY_ONLY_PERMISSIONS]);

/**
 * Permissions in `wanted` that `actorPermissions` does not cover. Driver-app and notify-only
 * permissions are left out: they give no power over the tenant (staff never hold the first; the
 * second only choose a channel of alerts), so they are not an escalation.
 */
export function missingGrants(actorPermissions: ReadonlySet<string>, wanted: Iterable<string>): string[] {
  const missing = new Set<string>();
  for (const p of wanted) if (!actorPermissions.has(p) && !NO_POWER.has(p)) missing.add(p);
  return [...missing].sort();
}

export class GrantDeniedError extends Error {
  readonly code = 'FORBIDDEN';
  constructor(readonly missing: string[]) {
    super(`Cannot grant permissions the actor does not hold: ${missing.join(', ')}`);
    this.name = 'GrantDeniedError';
  }
}

/**
 * Nobody hands out (or takes away, or deletes a role with) permissions they do not hold themselves.
 * Throws `GrantDeniedError`; use `missingGrants` for the non-throwing form.
 */
export function assertCanGrant(actorPermissions: ReadonlySet<string>, grantedPermissions: Iterable<string>): void {
  const missing = missingGrants(actorPermissions, grantedPermissions);
  if (missing.length) throw new GrantDeniedError(missing);
}

const deny = (reason: ManageDenial): ManageDecision => ({ ok: false, reason });

export function canManage(actor: ManageActor, target: ManageTarget, op: ManageOperation, options: ManageOptions = {}): ManageDecision {
  if (!actor.actorPermissions.has(MANAGE_OPERATION_PERMISSION[op])) return deny('MISSING_PERMISSION');
  if (target.targetId !== null && target.targetId === actor.actorId) return deny('SELF');

  const touchesOwner = target.targetIsOwner || target.nextIsOwner === true;
  if (touchesOwner && !actor.actorIsOwner) return deny('OWNER_ONLY');

  if (missingGrants(actor.actorPermissions, target.targetPermissions).length) return deny('EXCEEDS_ACTOR_PERMISSIONS');
  if (target.nextPermissions && missingGrants(actor.actorPermissions, target.nextPermissions).length) return deny('EXCEEDS_ACTOR_PERMISSIONS');

  if (options.activeOwnersAfter !== undefined && options.activeOwnersAfter < 1) return deny('LAST_OWNER');
  if (target.targetIsOwner && MAY_REMOVE_OWNER.has(op) && options.activeOwnersAfter === undefined) return deny('LAST_OWNER');

  return { ok: true };
}
